import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Navigation, Sparkles, Gem, Scissors, HeartHandshake, CalendarCheck, Shirt, MessageCircle, Star } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useContent } from "@/contexts/ContentContext";
import { useSettings } from "@/contexts/SettingsContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import { CATEGORY_KEYS } from "@/i18n";
import api from "@/lib/api";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=K%C3%B6nigstra%C3%9Fe%2036%2C%2070173%20Stuttgart";
const WHY_ICONS = [HeartHandshake, Gem, Scissors, Sparkles];
const HOW_ICONS = [CalendarCheck, Shirt, MessageCircle];

export default function Landing() {
  const { t } = useLang();
  const { c } = useContent();
  const { img } = useSettings();
  const [featured, setFeatured] = useState([]);

  useEffect(() => { api.get("/products").then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {}); }, []);

  const whyus = c("whyus", []);
  const how = c("howitworks", []);
  const testimonials = c("testimonials", []);

  return (
    <div className="relative min-h-screen">
      <div className="vl-noise" />
      <Header />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        <img src={img("hero")} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--vl-bg)] via-[var(--vl-bg)]/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--vl-bg)] via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 w-full">
          <div className="max-w-2xl vl-fade-up">
            <div className="text-xs sm:text-sm tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-6">{t("hero_eyebrow")}</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl font-light leading-[1.05] text-[var(--vl-ivory)]">{c("hero_title", t("hero_title"))}</h1>
            <p className="text-base sm:text-lg text-[var(--vl-stone)] mt-7 leading-relaxed max-w-xl font-light">{c("hero_subtitle", t("hero_sub"))}</p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/collecties" className="vl-btn-gold rounded-full px-8 py-4 text-sm tracking-widest uppercase font-medium flex items-center gap-2" data-testid="hero-collections-btn">{t("discover_collection")} <ArrowRight size={17} /></Link>
              <Link to="/termin" className="vl-btn-outline rounded-full px-8 py-4 text-sm tracking-widest uppercase" data-testid="hero-book-btn">{t("nav_appointment")}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section id="collections" className="relative z-10 py-24 sm:py-32 px-6 sm:px-8" data-testid="collections-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-3">{t("collections_sub")}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light text-[var(--vl-ivory)]">{t("collections_title")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORY_KEYS.map((cat, i) => (
              <Link to={`/collectie/${cat}`} key={cat} className={`vl-card rounded-2xl overflow-hidden group block ${i === 0 ? "lg:col-span-2" : ""}`} data-testid={`collection-card-${cat}`}>
                <div className={`relative overflow-hidden ${i === 0 ? "h-72" : "h-64"}`}>
                  <img src={img(`cat_${cat}`)} alt={t(`cat_${cat}`)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif-display text-2xl img-caption">{t(`cat_${cat}`)}</h3>
                    <p className="text-sm img-caption-sub mt-1">{t(`cat_${cat}_desc`)}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="relative z-10 py-16 px-6 sm:px-8" data-testid="featured-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-3">{t("featured_sub")}</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[var(--vl-ivory)]">{t("featured_title")}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {featured.map((p) => (
                <Link to={`/collectie/${p.category}`} key={p.id} className="vl-card rounded-2xl overflow-hidden group" data-testid={`featured-${p.id}`}>
                  <div className="h-72 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-display text-xl text-[var(--vl-ivory)]">{p.title}</h3>
                    {p.description && <p className="text-sm text-[var(--vl-stone)] mt-1 line-clamp-2">{p.description}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY US */}
      {whyus.length > 0 && (
        <section className="relative z-10 py-20 px-6 sm:px-8" data-testid="whyus-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-3">{t("whyus_sub")}</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[var(--vl-ivory)]">{t("whyus_title")}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyus.map((w, i) => {
                const Icon = WHY_ICONS[i % WHY_ICONS.length];
                return (
                  <div key={i} className="vl-card rounded-2xl p-7 text-center">
                    <div className="mx-auto w-12 h-12 rounded-full border border-[var(--vl-gold)]/50 flex items-center justify-center mb-5"><Icon size={22} className="text-[var(--vl-gold)]" /></div>
                    <h3 className="font-serif-display text-xl text-[var(--vl-ivory)]">{w.title}</h3>
                    <p className="text-sm text-[var(--vl-stone)] mt-2 leading-relaxed">{w.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      {how.length > 0 && (
        <section className="relative z-10 py-20 px-6 sm:px-8" data-testid="how-section">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-3">{t("how_sub")}</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[var(--vl-ivory)]">{t("how_title")}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {how.map((h, i) => {
                const Icon = HOW_ICONS[i % HOW_ICONS.length];
                return (
                  <div key={i} className="text-center">
                    <div className="mx-auto w-16 h-16 rounded-full bg-[var(--vl-surface)] border border-[var(--vl-border)] flex items-center justify-center mb-5 relative">
                      <Icon size={26} className="text-[var(--vl-gold)]" />
                      <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[var(--vl-gold)] text-[var(--vl-obsidian)] text-sm font-medium flex items-center justify-center">{i + 1}</span>
                    </div>
                    <h3 className="font-serif-display text-xl text-[var(--vl-ivory)]">{h.title}</h3>
                    <p className="text-sm text-[var(--vl-stone)] mt-2 leading-relaxed">{h.text}</p>
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-12"><Link to="/termin" className="vl-btn-gold rounded-full px-8 py-4 text-sm tracking-widest uppercase font-medium inline-flex items-center gap-2">{t("plan_appointment")} <ArrowRight size={16} /></Link></div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="relative z-10 py-20 px-6 sm:px-8" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-3">{t("testimonials_sub")}</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[var(--vl-ivory)]">{t("testimonials_title")}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {testimonials.map((r, i) => (
                <div key={i} className="vl-card rounded-2xl p-7">
                  <div className="flex gap-0.5 mb-4">{Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} size={15} className="text-[var(--vl-gold)] fill-[var(--vl-gold)]" />)}</div>
                  <p className="text-sm text-[var(--vl-ivory)]/90 leading-relaxed italic">“{r.text}”</p>
                  <div className="text-sm text-[var(--vl-gold)] mt-4 tracking-wider">— {r.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ABOUT */}
      <section className="relative z-10 py-20 px-6 sm:px-8" data-testid="about-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-2xl overflow-hidden h-80 lg:h-[26rem]">
            <img src={img("store")} alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-4">Atelier · Stuttgart</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[var(--vl-ivory)] leading-tight">{c("about_title", t("about_title"))}</h2>
            <p className="text-base text-[var(--vl-stone)] mt-6 leading-relaxed font-light">{c("about_text", t("about_text"))}</p>
            <Link to="/over-ons" className="vl-btn-outline rounded-full px-7 py-3.5 text-sm tracking-widest uppercase inline-flex items-center gap-2 mt-7">{t("learn_more")} <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="location" className="relative z-10 py-20 px-6 sm:px-8" data-testid="location-section">
        <div className="max-w-7xl mx-auto vl-card rounded-2xl p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-4">{t("nav_location")}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[var(--vl-ivory)]">{t("visit_title")}</h2>
            <p className="flex items-start gap-3 text-[var(--vl-stone)] mt-6"><MapPin size={20} className="text-[var(--vl-gold)] mt-0.5" /> Königstraße 36, 70173 Stuttgart, Deutschland</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="vl-btn-gold rounded-full px-7 py-3.5 text-sm tracking-widest uppercase font-medium inline-flex items-center gap-2" data-testid="open-maps-btn"><Navigation size={16} /> {t("open_maps")}</a>
              <Link to="/termin" className="vl-btn-outline rounded-full px-7 py-3.5 text-sm tracking-widest uppercase inline-flex items-center gap-2">{t("nav_appointment")}</Link>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden h-72 border border-[var(--vl-border)]">
            <iframe title="map" className="w-full h-full grayscale-[30%]" src="https://www.google.com/maps?q=K%C3%B6nigstra%C3%9Fe%2036%2C%2070173%20Stuttgart&output=embed" loading="lazy" />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative z-10 py-20 px-6 sm:px-8" data-testid="contact-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs tracking-[0.35em] uppercase text-[var(--vl-gold)] mb-3">Veloura</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-[var(--vl-ivory)]">{t("contact_title")}</h2>
            <p className="text-sm text-[var(--vl-stone)] mt-3">{t("contact_sub")}</p>
          </div>
          <ContactForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
